import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { SPACING, RADIUS } from '@backrs_ui/common-ui/src/designSystem/spacing/spacing';

import { Box, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ChartOptions,
  ChartData,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, annotationPlugin);

const BAR_CHART_HEIGHT = 24;
const AVERAGE_LINE_WIDTH = 12;

type ChartTicksProps = Readonly<{
  chartMin: number;
  chartMax: number;
  ticks: number[];
}>;

const ChartTicks = ({ chartMin, chartMax, ticks }: ChartTicksProps) => {
  return (
    <Box sx={ticksContainerSx}>
      {ticks.map((tick) => {
        const positionPercent = ((tick - chartMin) / (chartMax - chartMin)) * 100;

        // Determine tick position type
        const isFirstTick = tick === ticks[0];
        const isLastTick = tick === ticks[ticks.length - 1];

        const getTransform = () => {
          if (isFirstTick) {
            return 'translateX(0)';
          }

          if (isLastTick) {
            return 'translateX(-100%)';
          }

          return 'translateX(-50%)';
        };

        const getTextAlign = () => {
          if (isFirstTick) {
            return 'left';
          }

          if (isLastTick) {
            return 'right';
          }

          return 'center';
        };

        return (
          <Box
            key={tick}
            sx={{
              ...individualTickContainerSx,
              left: `${positionPercent}%`,
              transform: getTransform(),
              textAlign: getTextAlign(),
            }}
          >
            <Typography
              variant="text-xs-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {/* only show 1 decimal place if the tick is between 1 and 5 */}
              {tick.toFixed(tick > 0 && tick < 5 && ticks[ticks.length - 1] < 10 ? 1 : 0)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const tooltipArrow = `<div id="tooltip-arrow" style="
              position: absolute;
              width: 0;
              height: 0;
              left: 50%;
              bottom: -5px;
              transform: translateX(-50%);
              border-left: 6px solid transparent;
              border-right: 6px solid transparent;
              border-top: 6px solid #0D0D0D;">
            </div>`;

export type BarChartProps = Readonly<{
  chartMin: number;
  chartMax: number;
  ticks: number[];

  dataMin: number;
  dataAverage: number;
  dataMax: number;
}>;

export const BarChart = ({
  chartMin,
  chartMax,
  ticks,
  dataMin,
  dataAverage,
  dataMax,
}: BarChartProps) => {
  const isMobile = useMediaQuery('(max-width: 600px)');

  const data: ChartData<'bar'> = {
    labels: [''],
    datasets: [
      {
        label: '',
        data: [[dataMax, dataMin]],
        backgroundColor: COLOR_TOKENS['Brand/500'],
        borderRadius: 6,
        borderWidth: 1,
        borderColor: COLOR_TOKENS['Colors/Base/Black'],
        borderSkipped: false,
        barThickness: BAR_CHART_HEIGHT,
      },
    ],
  };

  // Chart options, including the annotation for the average line
  const options: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        min: chartMin,
        max: chartMax,
        display: false,
        grid: { display: false },
        border: { display: false },
      },
      y: {
        display: false,
        grid: { display: false },
        border: { display: false },
        ticks: { padding: 0 },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: function (context) {
          const tooltip = context.tooltip;

          let tooltipEl = document.getElementById('custom-tooltip');

          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'custom-tooltip';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.background = COLOR_TOKENS['Colors/Base/Black'];
            tooltipEl.style.color = COLOR_TOKENS['Colors/Base/White'];
            tooltipEl.style.padding = `${SPACING['spacing-sm']} ${SPACING['spacing-lg']}`;
            tooltipEl.style.borderRadius = RADIUS['radius-md'];
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.textAlign = 'center';
            tooltipEl.style.zIndex = '999';

            tooltipEl.innerHTML = tooltipArrow;

            document.body.appendChild(tooltipEl);
          }

          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = '0';
          } else {
            // Reset the tooltip content before updating it
            tooltipEl.innerHTML = `${tooltipArrow}<div style="font-size: 12px; padding-top: 4px; padding-bottom: 4px;">
            <p style="margin: 0;">50th percentile: ${dataAverage}</p>
            </div>`;

            const chartRect = context.chart.canvas.getBoundingClientRect();
            const tooltipX =
              chartRect.left +
              context.chart.scales.x.getPixelForValue(dataAverage) -
              tooltipEl.offsetWidth / 2;
            const tooltipY =
              chartRect.top +
              context.chart.scales.y.getPixelForValue(0) -
              tooltipEl.offsetHeight -
              20;

            tooltipEl.style.left = `${tooltipX}px`;
            tooltipEl.style.top = `${tooltipY}px`;
            tooltipEl.style.opacity = '1';
          }
        },
      },
      annotation: {
        annotations:
          dataAverage === 0
            ? []
            : {
                averageLine: {
                  type: 'line',
                  borderColor: COLOR_TOKENS['Colors/Base/Black'],
                  borderWidth: AVERAGE_LINE_WIDTH * (isMobile ? 0.5 : 1),
                  scaleID: 'x',
                  value: dataAverage,
                },
              },
      },
    },
  };

  return (
    <Box sx={chartContainerSx}>
      <Box sx={lineChartContainerSx}>
        <Bar
          data={data}
          options={options}
          // subtract 1 from the height to account for border width
          height={BAR_CHART_HEIGHT - 1}
          width={300}
        />
      </Box>

      <ChartTicks chartMin={chartMin} chartMax={chartMax} ticks={ticks} />
    </Box>
  );
};

const ticksContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  marginTop: backrs.spacing['spacing-2xl'],
});

const individualTickContainerSx: SxProps<Theme> = {
  position: 'absolute',
};

const chartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  height: `${backrs.spacing['spacing-7xl']}`,
  width: '100%',
});

const lineChartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: `${BAR_CHART_HEIGHT}px`,
  overflow: 'hidden',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
  borderRadius: backrs.radius['radius-sm'],
  position: 'relative',
  boxSizing: 'border-box',
});
